import type { SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PriceInput } from "@/components/common/PriceInput";
import { getFieldErrorMessage, useFieldContext, useShowFieldError } from "./form.context";

type Props = {
  label?: string;
  min?: number;
  max?: number;
  suffix?: string;
  width?: number | string;
  disabled?: boolean;
  variant?: "standard" | "outlined" | "filled";
  sx?: SxProps<Theme>;
  fallback?: number;
  legend?: boolean;
};

export function PriceField({
  min = 0,
  suffix = "€",
  width,
  disabled,
  variant = "outlined",
  sx,
  fallback,
  legend = false,
  label,
}: Props) {
  const field = useFieldContext<number>();
  const { t } = useTranslation();
  const errors = field.state.meta.errors;
  const hasError = useShowFieldError(errors);
  const errorMessage = getFieldErrorMessage(errors);
  const isEmpty = field.state.value === null || field.state.value === undefined;
  const displayValue = isEmpty && fallback !== undefined ? fallback : (field.state.value ?? 0);

  const labelStyles = label
    ? {
        "& .MuiInputLabel-root": { fontWeight: 600, color: "text.secondary" },
        "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
      }
    : {};

  const wLegend = legend
    ? {
        ...sx,
        ...labelStyles,
        "& .MuiFormHelperText-root": {
          marginLeft: 0,
        },
      }
    : {
        ...sx,
        ...labelStyles,
        "& .MuiFormHelperText-root": {
          marginLeft: 0,
        },
        "& fieldset": {
          top: 0,
          "& legend": {
            display: "none",
          },
        },
      };

  return (
    <PriceInput
      label={label}
      value={Number.isFinite(displayValue) ? displayValue : 0}
      onChange={(next) => field.handleChange(next)}
      onBlur={field.handleBlur}
      min={min}
      suffix={suffix}
      error={hasError}
      helperText={hasError ? t(errorMessage ?? "") : undefined}
      disabled={disabled}
      variant={variant}
      size="small"
      fullWidth={!width}
      sx={width ? { width, ...wLegend } : wLegend}
    />
  );
}
